import "./styles.css";
import { ApiError, ControlApi } from "./api";
import type { AgentRecord, StatsResponse, TestConfig } from "./types";

const elements = {
  connectionForm: required<HTMLFormElement>("connectionForm"),
  configForm: required<HTMLFormElement>("configForm"),
  apiUrl: required<HTMLInputElement>("apiUrl"),
  adminToken: required<HTMLInputElement>("adminToken"),
  connectionIndicator: required<HTMLElement>("connectionIndicator"),
  connectionText: required<HTMLElement>("connectionText"),
  refreshButton: required<HTMLButtonElement>("refreshButton"),
  lastSync: required<HTMLElement>("lastSync"),
  totalAgents: required<HTMLElement>("totalAgents"),
  onlineAgents: required<HTMLElement>("onlineAgents"),
  offlineAgents: required<HTMLElement>("offlineAgents"),
  onlineRatio: required<HTMLElement>("onlineRatio"),
  testStatus: required<HTMLElement>("testStatus"),
  currentChannel: required<HTMLElement>("currentChannel"),
  active: required<HTMLInputElement>("active"),
  activeLabel: required<HTMLElement>("activeLabel"),
  channel: required<HTMLInputElement>("channel"),
  testUrl: required<HTMLInputElement>("testUrl"),
  intervalSeconds: required<HTMLInputElement>("intervalSeconds"),
  refreshMinutes: required<HTMLInputElement>("refreshMinutes"),
  stopButton: required<HTMLButtonElement>("stopButton"),
  saveButton: required<HTMLButtonElement>("saveButton"),
  autoRefresh: required<HTMLInputElement>("autoRefresh"),
  agentSearch: required<HTMLInputElement>("agentSearch"),
  statusFilter: required<HTMLSelectElement>("statusFilter"),
  agentTableBody: required<HTMLTableSectionElement>("agentTableBody"),
  tableSummary: required<HTMLElement>("tableSummary"),
  generatedAt: required<HTMLElement>("generatedAt"),
  toast: required<HTMLElement>("toast")
};

let api: ControlApi | null = null;
let currentConfig: TestConfig | null = null;
let currentStats: StatsResponse | null = null;
let refreshTimer: number | null = null;
let toastTimer: number | null = null;
let requestInFlight = false;

initialize();

function initialize(): void {
  const defaultApiUrl =
    import.meta.env.VITE_DEFAULT_API_URL ??
    sessionStorage.getItem("embedHealthApiUrl") ??
    "http://localhost:8787";

  elements.apiUrl.value = defaultApiUrl;
  setConnected(false);
  updateActiveLabel();

  elements.connectionForm.addEventListener("submit", connect);
  elements.configForm.addEventListener("submit", saveConfig);
  elements.stopButton.addEventListener("click", stopTest);
  elements.refreshButton.addEventListener("click", () => {
    void refreshAll(true);
  });
  elements.active.addEventListener("change", updateActiveLabel);
  elements.autoRefresh.addEventListener("change", scheduleAutoRefresh);
  elements.agentSearch.addEventListener("input", renderAgents);
  elements.statusFilter.addEventListener("change", renderAgents);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && api && elements.autoRefresh.checked) {
      void refreshAll(false);
    }
  });
}

async function connect(event: SubmitEvent): Promise<void> {
  event.preventDefault();

  const token = elements.adminToken.value.trim();
  if (!token) {
    showToast("Admin token alanını doldurun.", "error");
    return;
  }

  setBusy(true);
  try {
    const nextApi = new ControlApi(elements.apiUrl.value, token);
    await nextApi.health();
    api = nextApi;

    sessionStorage.setItem(
      "embedHealthApiUrl",
      elements.apiUrl.value.trim()
    );

    await refreshAll(false);
    setConnected(true);
    scheduleAutoRefresh();
    showToast("Control API bağlantısı kuruldu.", "success");
  } catch (error) {
    api = null;
    currentConfig = null;
    currentStats = null;
    setConnected(false);
    showError(error);
  } finally {
    setBusy(false);
  }
}

async function refreshAll(showSuccess: boolean): Promise<void> {
  if (!api || requestInFlight) {
    return;
  }

  requestInFlight = true;
  elements.refreshButton.classList.add("is-spinning");

  try {
    const [config, stats] = await Promise.all([
      api.getConfig(),
      api.getStats()
    ]);

    currentConfig = config;
    currentStats = stats;
    renderConfig(config);
    renderStats(stats);
    setConnected(true);

    const now = new Date();
    elements.lastSync.textContent = formatDateTime(now);
    if (showSuccess) {
      showToast("Veriler güncellendi.", "success");
    }
  } catch (error) {
    setConnected(false);
    showError(error);
  } finally {
    requestInFlight = false;
    elements.refreshButton.classList.remove("is-spinning");
  }
}

async function saveConfig(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!api) {
    showToast("Önce Control API’ye bağlanın.", "error");
    return;
  }

  const active = elements.active.checked;
  const channel = elements.channel.value.trim();
  const url = elements.testUrl.value.trim();
  const intervalSeconds = Number(elements.intervalSeconds.value);
  const refreshMinutes = Number(elements.refreshMinutes.value);

  if (active && (!channel || !url)) {
    showToast("Aktif test için kanal ve URL gereklidir.", "error");
    return;
  }

  setBusy(true);
  try {
    currentConfig = await api.saveConfig({
      active,
      channel,
      url,
      intervalSeconds,
      refreshMinutes
    });

    renderConfig(currentConfig);
    await refreshAll(false);
    showToast("Test yapılandırması kaydedildi.", "success");
  } catch (error) {
    showError(error);
  } finally {
    setBusy(false);
  }
}

async function stopTest(): Promise<void> {
  if (!api || !currentConfig) {
    return;
  }

  setBusy(true);
  try {
    currentConfig = await api.saveConfig({
      active: false,
      channel: currentConfig.channel,
      url: "",
      intervalSeconds: currentConfig.intervalSeconds,
      refreshMinutes: currentConfig.refreshMinutes
    });

    renderConfig(currentConfig);
    await refreshAll(false);
    showToast("Test oturumu durduruldu.", "success");
  } catch (error) {
    showError(error);
  } finally {
    setBusy(false);
  }
}

function renderConfig(config: TestConfig): void {
  elements.active.checked = config.active;
  elements.channel.value = config.channel;
  elements.testUrl.value = config.url;
  elements.intervalSeconds.value = String(config.intervalSeconds);
  elements.refreshMinutes.value = String(config.refreshMinutes);
  updateActiveLabel();
}

function renderStats(stats: StatsResponse): void {
  elements.totalAgents.textContent = formatNumber(stats.totalAgents);
  elements.onlineAgents.textContent = formatNumber(stats.onlineAgents);
  elements.offlineAgents.textContent = formatNumber(stats.offlineAgents);

  const ratio =
    stats.totalAgents === 0
      ? 0
      : Math.round((stats.onlineAgents / stats.totalAgents) * 100);
  elements.onlineRatio.textContent = `%${ratio} cihaz güncel`;

  elements.testStatus.textContent = stats.active ? "AKTİF" : "PASİF";
  elements.testStatus.classList.toggle("is-active", stats.active);
  elements.currentChannel.textContent = stats.currentChannel
    ? `Kanal: ${stats.currentChannel}`
    : "Kanal seçilmedi";
  elements.generatedAt.textContent =
    `Üretildi: ${formatDateTime(new Date(stats.generatedAtUtc))}`;

  renderAgents();
}

function renderAgents(): void {
  const agents = currentStats?.agents ?? [];
  const search = elements.agentSearch.value.trim().toLocaleLowerCase("tr");
  const filter = elements.statusFilter.value;

  const visible = agents.filter((agent) => {
    const searchable =
      `${agent.agentId} ${agent.channel} ${agent.version}`.toLocaleLowerCase("tr");
    const matchesSearch = !search || searchable.includes(search);
    const matchesFilter =
      filter === "all" ||
      (filter === "online" && agent.online) ||
      (filter === "offline" && !agent.online) ||
      (filter === "error" && agent.status === "error");

    return matchesSearch && matchesFilter;
  });

  const rows = visible.map(createAgentRow);
  if (rows.length === 0) {
    const row = document.createElement("tr");
    row.className = "empty-row";
    const cell = document.createElement("td");
    cell.colSpan = 6;
    cell.textContent =
      agents.length === 0
        ? "Henüz agent heartbeat kaydı bulunmuyor."
        : "Filtreyle eşleşen agent bulunamadı.";
    row.append(cell);
    rows.push(row);
  }

  elements.agentTableBody.replaceChildren(...rows);
  elements.tableSummary.textContent =
    `${visible.length} / ${agents.length} kayıt gösteriliyor`;
}

function createAgentRow(agent: AgentRecord): HTMLTableRowElement {
  const row = document.createElement("tr");

  row.append(
    textCell(agent.agentId, "agent-name"),
    statusCell(agent),
    textCell(agent.channel || "—"),
    browserCell(agent.browserAlive),
    textCell(relativeTime(agent.lastSeenUtc)),
    textCell(agent.version)
  );

  if (agent.lastError) {
    row.title = agent.lastError;
  }

  return row;
}

function statusCell(agent: AgentRecord): HTMLTableCellElement {
  const cell = document.createElement("td");
  const badge = document.createElement("span");
  const state = agent.status === "error"
    ? "error"
    : agent.status === "stopped"
      ? "stopped"
      : agent.online
        ? "online"
        : "offline";

  badge.className = `status-badge status-badge--${state}`;
  badge.textContent =
    state === "online"
      ? "Çevrimiçi"
      : state === "error"
        ? "Hata"
        : state === "stopped"
          ? "Durduruldu"
          : "Çevrimdışı";
  cell.append(badge);
  return cell;
}

function browserCell(alive: boolean): HTMLTableCellElement {
  const cell = document.createElement("td");
  const value = document.createElement("span");
  value.className = alive ? "browser-state is-alive" : "browser-state";
  value.textContent = alive ? "Çalışıyor" : "Kapalı";
  cell.append(value);
  return cell;
}

function textCell(value: string, className?: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  cell.textContent = value;
  if (className) {
    cell.className = className;
  }
  return cell;
}

function updateActiveLabel(): void {
  elements.activeLabel.textContent = elements.active.checked ? "Aktif" : "Pasif";
}

function scheduleAutoRefresh(): void {
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }

  if (api && elements.autoRefresh.checked) {
    refreshTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refreshAll(false);
      }
    }, 30_000);
  }
}

function setConnected(connected: boolean): void {
  elements.connectionIndicator.classList.toggle("is-connected", connected);
  elements.connectionText.textContent = connected ? "API bağlı" : "Bağlı değil";
  elements.refreshButton.disabled = !connected;
  elements.saveButton.disabled = !connected;
  elements.stopButton.disabled = !connected;
}

function setBusy(busy: boolean): void {
  elements.connectionForm
    .querySelectorAll("button, input")
    .forEach((element) => {
      (element as HTMLButtonElement | HTMLInputElement).disabled = busy;
    });

  elements.saveButton.disabled = busy || !api;
  elements.stopButton.disabled = busy || !api;
}

function showError(error: unknown): void {
  if (error instanceof ApiError) {
    const suffix = error.requestId ? ` İstek: ${error.requestId}` : "";
    showToast(`${error.message}${suffix}`, "error");
    return;
  }

  showToast(
    error instanceof Error ? error.message : "Beklenmeyen bir hata oluştu.",
    "error"
  );
}

function showToast(message: string, type: "success" | "error"): void {
  if (toastTimer !== null) {
    window.clearTimeout(toastTimer);
  }

  elements.toast.textContent = message;
  elements.toast.className = `toast is-visible toast--${type}`;
  toastTimer = window.setTimeout(() => {
    elements.toast.classList.remove("is-visible");
  }, 5_000);
}

function relativeTime(value: string): string {
  const date = new Date(value);
  const seconds = Math.round((date.getTime() - Date.now()) / 1_000);
  const formatter = new Intl.RelativeTimeFormat("tr", { numeric: "auto" });

  if (Math.abs(seconds) < 60) {
    return formatter.format(seconds, "second");
  }

  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) {
    return formatter.format(minutes, "minute");
  }

  const hours = Math.round(minutes / 60);
  return formatter.format(hours, "hour");
}

function formatDateTime(value: Date): string {
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "medium",
    timeStyle: "medium"
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("tr-TR").format(value);
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Arayüz öğesi bulunamadı: ${id}`);
  }
  return element as T;
}
